import { ImageSize } from './domain';
import {
    ImageUploadAdapterMode,
    UploadImageNormalizationPayload,
    UploadOriginalImageResponse
} from './upload-storage';

export interface UploadOriginalImageInput {
    file: Blob;
    imageSize?: ImageSize | null;
    mimeType?: string | null;
    byteSize?: number | null;
}

export interface ImageUploadService {
    uploadOriginalImage(input: UploadOriginalImageInput): Promise<UploadOriginalImageResponse | null>;
}

export interface ImageUploadApiServiceOptions {
    endpoint?: string;
    fetchImpl?: ImageUploadFetch;
    fallbackOnNetworkError?: boolean;
}

export interface RuntimeImageUploadConfig {
    adapter: ImageUploadAdapterMode;
    endpoint: string;
}

export interface ImageUploadHttpResponse {
    ok: boolean;
    status: number;
    json?: () => Promise<unknown>;
    text?: () => Promise<string>;
}

export type ImageUploadFetch = (input: string, init: {
    method: string;
    body: FormData;
}) => Promise<ImageUploadHttpResponse>;

export const defaultImageUploadApiEndpoint = '/api/lookit/uploads';

const genericUploadFileName = 'lookit-upload';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function payloadRecord(payload: unknown): Record<string, unknown> | null {
    if (!isRecord(payload)) return null;
    if (isRecord(payload.data)) {
        if (typeof payload.data.upload_image_id === 'string') return payload.data;
        if (isRecord(payload.data.upload)) return payload.data.upload;
    }
    if (typeof payload.upload_image_id === 'string') return payload;
    if (isRecord(payload.upload)) return payload.upload;
    return null;
}

function numberField(record: Record<string, unknown>, key: string): number | null {
    const value = record[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringField(record: Record<string, unknown>, key: string): string | null {
    const value = record[key];
    return typeof value === 'string' && value.trim() ? value : null;
}

function booleanField(record: Record<string, unknown>, key: string): boolean | undefined {
    const value = record[key];
    return typeof value === 'boolean' ? value : undefined;
}

function normalizeUploadImageResponse(payload: unknown): UploadOriginalImageResponse | null {
    const record = payloadRecord(payload);
    if (!record) return null;

    const uploadImageId = stringField(record, 'upload_image_id');
    const mimeType = stringField(record, 'mime_type');
    const expiresAt = stringField(record, 'expires_at');
    const deleteToken = stringField(record, 'delete_token');
    const originalWidth = numberField(record, 'original_width');
    const originalHeight = numberField(record, 'original_height');
    const byteSize = numberField(record, 'byte_size');

    if (!uploadImageId || !mimeType || !expiresAt || !deleteToken || !originalWidth || !originalHeight || !byteSize) {
        return null;
    }

    const storageProvider = stringField(record, 'storage_provider') || 'local_mock';
    const createdAt = stringField(record, 'created_at') || new Date().toISOString();
    const readUrl = stringField(record, 'read_url');
    const readUrlExpiresAt = stringField(record, 'read_url_expires_at');
    const normalizedImageId = stringField(record, 'normalized_image_id');
    const normalizedWidth = numberField(record, 'normalized_width');
    const normalizedHeight = numberField(record, 'normalized_height');
    const normalization = isRecord(record.normalization) ? record.normalization as UploadImageNormalizationPayload : null;

    return {
        ok: true,
        upload_image_id: uploadImageId,
        original_width: originalWidth,
        original_height: originalHeight,
        mime_type: mimeType,
        byte_size: byteSize,
        expires_at: expiresAt,
        delete_token: deleteToken,
        storage_provider: storageProvider as UploadOriginalImageResponse['storage_provider'],
        created_at: createdAt,
        read_url: readUrl,
        read_url_expires_at: readUrlExpiresAt,
        normalized_image_id: normalizedImageId,
        normalized_width: normalizedWidth,
        normalized_height: normalizedHeight,
        orientation_handled: booleanField(record, 'orientation_handled'),
        exif_stripped: booleanField(record, 'exif_stripped'),
        external_transfer_ready: booleanField(record, 'external_transfer_ready'),
        normalization,
        direct_original_url_exposed: typeof record.direct_original_url_exposed === 'boolean'
            ? record.direct_original_url_exposed
            : Boolean(readUrl),
        url_log_policy: isRecord(record.url_log_policy)
            ? record.url_log_policy as unknown as UploadOriginalImageResponse['url_log_policy']
            : {
                original_url_requires_expiring_url: true,
                default_read_url_expires_in_seconds: 900,
                blocked_log_fields: ['read_url', 'delete_token', 'original_filename', 'file_name', 'image_url'],
                notes: '민감 업로드 값은 로그에 남기지 않는다.'
            }
    };
}

async function readJsonPayload(response: ImageUploadHttpResponse): Promise<unknown> {
    if (typeof response.json === 'function') {
        return response.json();
    }

    if (typeof response.text === 'function') {
        const text = await response.text();
        return text ? JSON.parse(text) : null;
    }

    return null;
}

function runtimeFetch(): ImageUploadFetch | null {
    const scope = typeof globalThis === 'undefined' ? null : globalThis as unknown as { fetch?: unknown };
    return typeof scope?.fetch === 'function' ? (scope.fetch as ImageUploadFetch) : null;
}

function createUploadRequestBody(input: UploadOriginalImageInput): FormData {
    const body = new FormData();
    body.append('image', input.file, genericUploadFileName);
    if (input.imageSize) body.set('imageSize', JSON.stringify(input.imageSize));
    if (input.mimeType) body.set('clientMimeType', input.mimeType);
    if (typeof input.byteSize === 'number' && Number.isFinite(input.byteSize)) {
        body.set('clientByteSize', String(input.byteSize));
    }
    return body;
}

export function createApiImageUploadService(options: ImageUploadApiServiceOptions = {}): ImageUploadService {
    const endpoint = options.endpoint || defaultImageUploadApiEndpoint;
    const fetchImpl = options.fetchImpl || runtimeFetch();
    const fallbackOnNetworkError = options.fallbackOnNetworkError !== false;

    return {
        async uploadOriginalImage(input: UploadOriginalImageInput): Promise<UploadOriginalImageResponse | null> {
            if (!fetchImpl) return null;

            try {
                const response = await fetchImpl(endpoint, {
                    method: 'POST',
                    body: createUploadRequestBody(input)
                });
                const payload = await readJsonPayload(response);
                return normalizeUploadImageResponse(payload);
            } catch (error) {
                if (!fallbackOnNetworkError) throw error;
                return null;
            }
        }
    };
}

export const mockImageUploadService: ImageUploadService = {
    async uploadOriginalImage(): Promise<UploadOriginalImageResponse | null> {
        return null;
    }
};

type RuntimeScope = {
    LOOKIT_IMAGE_UPLOAD_ADAPTER?: unknown;
    LOOKIT_IMAGE_UPLOAD_ENDPOINT?: unknown;
    __LOOKIT_CONFIG__?: {
        imageUploadAdapter?: unknown;
        imageUploadEndpoint?: unknown;
        imageUploadApiEndpoint?: unknown;
    };
    localStorage?: {
        getItem(key: string): string | null;
    };
};

function normalizedAdapterMode(value: unknown): ImageUploadAdapterMode | null {
    return value === 'api' || value === 'mock' ? value : null;
}

function safeLocalStorageGet(scope: RuntimeScope | null, key: string): string | null {
    try {
        return scope?.localStorage?.getItem(key) || null;
    } catch {
        return null;
    }
}

export function runtimeImageUploadConfig(): RuntimeImageUploadConfig {
    const scope = typeof globalThis === 'undefined' ? null : globalThis as unknown as RuntimeScope;
    const appConfig = scope?.__LOOKIT_CONFIG__;
    const adapter = normalizedAdapterMode(scope?.LOOKIT_IMAGE_UPLOAD_ADAPTER)
        || normalizedAdapterMode(appConfig?.imageUploadAdapter)
        || normalizedAdapterMode(safeLocalStorageGet(scope, 'lookit.imageUploadAdapter'))
        || 'mock';
    const endpoint = (
        typeof scope?.LOOKIT_IMAGE_UPLOAD_ENDPOINT === 'string' && scope.LOOKIT_IMAGE_UPLOAD_ENDPOINT
    ) || (
        typeof appConfig?.imageUploadEndpoint === 'string' && appConfig.imageUploadEndpoint
    ) || (
        typeof appConfig?.imageUploadApiEndpoint === 'string' && appConfig.imageUploadApiEndpoint
    ) || safeLocalStorageGet(scope, 'lookit.imageUploadEndpoint')
        || defaultImageUploadApiEndpoint;

    return { adapter, endpoint };
}

export function createRuntimeImageUploadService(): ImageUploadService {
    const config = runtimeImageUploadConfig();
    return config.adapter === 'api'
        ? createApiImageUploadService({ endpoint: config.endpoint })
        : mockImageUploadService;
}

let activeImageUploadService: ImageUploadService = createRuntimeImageUploadService();

export function setImageUploadService(service: ImageUploadService) {
    activeImageUploadService = service;
}

export function resetImageUploadService() {
    activeImageUploadService = mockImageUploadService;
}

export function useRuntimeImageUploadService() {
    activeImageUploadService = createRuntimeImageUploadService();
    return activeImageUploadService;
}

export async function uploadOriginalImage(input: UploadOriginalImageInput): Promise<UploadOriginalImageResponse | null> {
    return activeImageUploadService.uploadOriginalImage(input);
}

export const imageUploadService = {
    createApiImageUploadService,
    createRuntimeImageUploadService,
    runtimeImageUploadConfig,
    setImageUploadService,
    resetImageUploadService,
    useRuntimeImageUploadService,
    uploadOriginalImage
};
